import { useEffect, useRef, useState } from 'react';

// Same-origin RELATIVE path -> the BRNGG-54406 gateway/CDN reverse-proxy forwards
// it to https://help.bringg.com/v1/rss/en server-side. Relative (no host) so it
// resolves per-environment automatically: staging-app / edge-app / app.bringg.com
// each hit their own /help-center/rss. Same-origin avoids the CORS block that a
// direct help.bringg.com call hits.
const RSS_FEED_URL = '/help-center/rss';
const LOCAL_STORAGE_KEY = 'whats-new-last-seen';
const THIRTY_MINUTES_MS = 1800000;

function getLatestPubDate(xml: string): string | null {
	const doc = new DOMParser().parseFromString(xml, 'text/xml');
	const firstItem = doc.querySelector('item');
	if (!firstItem) return null;

	const pubDateEl = firstItem.querySelector('pubDate');
	if (!pubDateEl?.textContent) return null;

	const parsed = new Date(pubDateEl.textContent);
	if (isNaN(parsed.getTime())) return null;

	return pubDateEl.textContent;
}

function hasNewerContent(latestPubDate: string): boolean {
	const lastSeen = localStorage.getItem(LOCAL_STORAGE_KEY);
	if (!lastSeen) return true;

	return new Date(latestPubDate).getTime() > new Date(lastSeen).getTime();
}

export function useWhatsNewIndicator(enabled: boolean): { hasNewContent: boolean; markAsSeen: () => void } {
	const [hasNewContent, setHasNewContent] = useState(false);
	const latestPubDateRef = useRef<string | null>(null);

	useEffect(() => {
		if (!enabled) return;

		let cancelled = false;

		async function checkForNewContent() {
			try {
				const response = await fetch(RSS_FEED_URL);
				if (!response.ok || cancelled) return;

				const xml = await response.text();
				const pubDate = getLatestPubDate(xml);
				if (!pubDate || cancelled) return;

				latestPubDateRef.current = pubDate;
				setHasNewContent(hasNewerContent(pubDate));
			} catch (error) {
				// Silent failure -- indicator simply won't show
				console.error('useWhatsNewIndicator: failed to check for new content', error);
			}
		}

		void checkForNewContent();
		const intervalId = setInterval(() => void checkForNewContent(), THIRTY_MINUTES_MS);

		return () => {
			cancelled = true;
			clearInterval(intervalId);
		};
	}, [enabled]);

	function markAsSeen() {
		const pubDate = latestPubDateRef.current ?? new Date().toUTCString();

		try {
			localStorage.setItem(LOCAL_STORAGE_KEY, pubDate);
		} catch (error) {
			// localStorage may be full or unavailable
			console.error('useWhatsNewIndicator: failed to persist last-seen date', error);
		}
		setHasNewContent(false);
	}

	return { hasNewContent, markAsSeen };
}
